import React, { useEffect, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Typography,
  TextField,
  IconButton,
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon, Send as SendIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { api } from '../../api/chat';
import {
  refreshContacts,
  refreshConversations,
  refreshPendingRequests,
} from '../../redux/chat.redux';
import type { FriendRequest, Greeting } from '../../types/people';
import { avatarText, formatFullTime } from '../../utils/format';
import PanelShell from './panel-shell';

const GREETING_MAX = 50;

interface Props {
  onClose: () => void;
  embedded?: boolean;
}

export default function FriendRequestsPanel({ onClose, embedded }: Props) {
  const dispatch = useAppDispatch();
  const me = useAppSelector((s) => s.user.user);
  const pending = useAppSelector((s) => s.chat.pendingRequests);
  const [tab, setTab] = useState(0);
  const [all, setAll] = useState<FriendRequest[]>([]);
  const [replyText, setReplyText] = useState<Record<number, string>>({});

  const reload = () => {
    dispatch(refreshPendingRequests());
    api
      .friendRequestsAll()
      .then((r) => setAll(r))
      .catch(() => {});
  };

  useEffect(() => {
    setTab(0);
    reload();
    // eslint-disable-next-line
  }, []);

  const accept = async (id: number) => {
    try {
      await api.acceptFriendRequest(id);
      reload();
      dispatch(refreshContacts());
      dispatch(refreshConversations());
    } catch (err: any) {
      alert(err?.message || '操作失败');
    }
  };

  const reject = async (id: number) => {
    try {
      await api.rejectFriendRequest(id);
      reload();
    } catch (err: any) {
      alert(err?.message || '操作失败');
    }
  };

  // 双方均可回复打招呼(最长 50 字)
  const greet = async (id: number) => {
    const text = (replyText[id] || '').trim();
    if (!text) return;
    try {
      await api.greetFriendRequest(id, text);
      setReplyText((prev) => ({ ...prev, [id]: '' }));
      reload();
    } catch (err: any) {
      alert(err?.message || '发送失败');
    }
  };

  const incoming = pending.filter((r) => r.to_user_id === me?.id);
  const outgoing = all.filter((r) => r.from_user_id === me?.id);

  const renderGreetings = (r: FriendRequest) => {
    const greetings: Greeting[] = r.greetings || [];
    if (greetings.length === 0) return null;
    return (
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {greetings.map((g, i) => {
          const mine = g.from_user_id === me?.id;
          return (
            <Box
              key={i}
              sx={{
                display: 'flex',
                justifyContent: mine ? 'flex-end' : 'flex-start',
              }}
            >
              <Box
                sx={{
                  maxWidth: '85%',
                  px: 1.2,
                  py: 0.6,
                  borderRadius: mine ? '10px 10px 2px 10px' : '10px 10px 10px 2px',
                  bgcolor: mine ? 'primary.main' : '#fff',
                  color: mine ? '#fff' : 'text.primary',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                  wordBreak: 'break-word',
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                {g.content}
                <Typography
                  component="div"
                  variant="caption"
                  sx={{ color: mine ? 'rgba(255,255,255,0.7)' : 'text.secondary', display: 'block', textAlign: 'right', mt: 0.3 }}
                >
                  {formatFullTime(g.created_at)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderReplyBox = (r: FriendRequest) => (
    <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={`回复打招呼(≤${GREETING_MAX}字)`}
        value={replyText[r.id] || ''}
        onChange={(e) => setReplyText((prev) => ({ ...prev, [r.id]: e.target.value.slice(0, GREETING_MAX) }))}
        inputProps={{ maxLength: GREETING_MAX }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') greet(r.id);
        }}
      />
      <IconButton size="small" color="primary" onClick={() => greet(r.id)} disabled={!(replyText[r.id] || '').trim()}>
        <SendIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <PanelShell title="新的好友" onClose={onClose} embedded={embedded}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: '#fff' }}>
        <Tab label={`收到的申请(${incoming.length})`} />
        <Tab label={`我发出的(${outgoing.length})`} />
      </Tabs>
      <Box sx={{ p: 1.5 }}>
        {tab === 0 &&
          (incoming.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
              暂无好友申请
            </Typography>
          ) : (
            <List disablePadding>
              {incoming.map((r) => {
                const u = r.from_user;
                return (
                  <ListItem key={r.id} alignItems="flex-start" sx={{ px: 0.5, mb: 1, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    <ListItemAvatar sx={{ mt: 0.5 }}>
                      <Avatar src={u?.avatar || undefined}>{avatarText(u)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={u?.nickname || u?.username || '未知用户'}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" component="span">
                            请求加你为好友
                          </Typography>
                          {renderGreetings(r)}
                          {renderReplyBox(r)}
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button size="small" variant="contained" startIcon={<CheckIcon />} onClick={() => accept(r.id)}>
                              接受
                            </Button>
                            <Button size="small" variant="outlined" color="inherit" startIcon={<CloseIcon />} onClick={() => reject(r.id)}>
                              拒绝
                            </Button>
                          </Box>
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          ))}

        {tab === 1 &&
          (outgoing.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
              暂无发出的申请
            </Typography>
          ) : (
            <List disablePadding>
              {outgoing.map((r) => {
                const u = r.to_user;
                const statusText = r.status === 'pending' ? '等待对方确认' : r.status === 'accepted' ? '已通过' : '已拒绝';
                return (
                  <ListItem key={r.id} alignItems="flex-start" sx={{ px: 0.5, mb: 1, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    <ListItemAvatar sx={{ mt: 0.5 }}>
                      <Avatar src={u?.avatar || undefined}>{avatarText(u)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={u?.nickname || u?.username || '未知用户'}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" component="span">
                            {statusText}
                          </Typography>
                          {renderGreetings(r)}
                          {r.status === 'pending' && renderReplyBox(r)}
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          ))}
      </Box>
    </PanelShell>
  );
}
